import { getCountryFromSideFilters, getProductGroupFromSideFilters } from '@utils/FilterUtils';
import { getPageTypeWithPageName, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { concatenateSelectedFeatureName, doAmplitudeAnalyticsTracking } from '../PageAnalytics';
import { AnalyticTrackingBasePropertiesObj, TrackingGeneralProp } from './AnalyticTrackingBasePropertiesObj';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';

//Example of Chart Interaction tracking
/**
 analytics.track("Chart Interactions", {
 "access": "Retail Insights",
 "chartName": "Performance Trend Chart", //Or "Performance Card"
 "locationId": "Germany",
 "productGroup": "PTV", //"All", "PTV, AIR CONDITIONER"
 "elementName": "Show",
 "elementType": "Filter",
 "optionChosen": "Revenue", //"Revenue" or "Units"
 "pageName": "Retailer Overview",
 "section": "Retail Intelligence",
 "subsection": "Retailer Overview"
 })
 **/

export const CHART_RADIO_BUTTON_TRACKING_TITLE = 'Chart Interactions';

//Extend base properties
export interface ChartInteractionTracking extends AnalyticTrackingBasePropertiesObj {
  chartName: string;
  elementName: string;
  elementType: string;
  optionChosen: string;
}

export enum ChartElementName {
  BY = 'By',
  CHART_HOVER = 'Chart hover',
  KPI = 'KPI',
  SHOW = 'Show',
  SORT_BY = 'Sort by',
  TABLE_SORT = 'Table Sort',
  SHOW_PERFORMANCE_WITHIN = 'Show performance within',
  DISPLAY_BY = 'Display by',
  DATA_POINT_SIZE = 'Data point size based on',
  PERIOD_SLIDER = 'Period slider',
  Play = 'Play',
  Pause = 'Pause',
  BY_SEGMENT = 'By segment',
  BRAND = 'Brand',
  WITH = 'With',
  FOR = 'For',
  AND = 'And',
  PERFORMANCE = 'Performance',
}

export enum ChartElementType {
  HOVER = 'Hover',
  FILTER = 'Filter',
  SORT = 'Sort',
  RADIO = 'Radio button',
  Button = 'Button',
}

export interface ChartInteractionTrackingFunctionParam {
  eventTitle: string;
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  elementName: ChartElementName;
  elementType: ChartElementType;
  optionChosen: string;
  chartName: string;
  access: Object;
  subSection?: SubSection;
}

export const chartInteractionTrackingFunction = (param: ChartInteractionTrackingFunctionParam) => {
  const {
    sideFilters,
    pageName,
    retailerClientState,
    elementName,
    elementType,
    optionChosen,
    chartName,
    track,
    eventTitle,
    pageType,
    access,
    subSection,
  } = param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const chartInteractionProperties: ChartInteractionTracking = {
    access: access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label),
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName: pageName,
    retailerDataFor: retailerClientState.clientName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: subSection ?? pageName,
    elementName: elementName,
    elementType: elementType,
    optionChosen: optionChosen,
    chartName: chartName,
  };

  doAmplitudeAnalyticsTracking(eventTitle, track, chartInteractionProperties);
};

/**
 * Subset data type for PageElementViewTrackingParam, returning all generic value
 */
export type ChartInteractionTrackingFunctionParamSubset = Pick<
  ChartInteractionTrackingFunctionParam,
  'eventTitle' | 'sideFilters' | 'retailerClientState' | 'track' | 'pageType'
>;

export type RetailerClientStateKey =
  | 'retailerClient'
  | 'hitlistsRetailerClient'
  | 'sellerInsightsRetailerClient'
  | 'marketPremiumRetailerClient';

/**
 * custom hook to generate pageElementViewParam common value for Product Analyzer page
 */
export const useGenerateChartInteractionTrackingParam: (
  retailerClientStateKey?: RetailerClientStateKey,
) => ChartInteractionTrackingFunctionParamSubset = (retailerClientStateKey = 'retailerClient') => {
  // Parameters for Amplitude Tracking
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state[retailerClientStateKey]);

  return {
    eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
    sideFilters: sideFiltersState.appliedFilters,
    retailerClientState: retailerClientState,
    track: track,
    pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
    pageName: retailerClientState.pageViewing,
  };
};
